import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Divider,
  InputAdornment
} from '@mui/material';
import { Login as LoginIcon, LockOutlined as LockIcon, Email as EmailIcon, Lock as PasswordIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

import NebenanHilfeLogo from '../UI/NebenanHilfeLogo';

const LoginForm = ({ onSwitchToRegister, onSwitchToReset }) => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    contact: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.contact || !formData.password) {
      setError('Bitte fülle alle Felder aus');
      return;
    }

    setLoading(true);
    const result = await login(formData.contact, formData.password);
    
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <Card sx={{ maxWidth: 440, mx: 'auto' }}>
      <CardContent sx={{ p: { xs: 4, md: 5 } }}>
        <Box textAlign="center" mb={3} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <NebenanHilfeLogo size="large" />
          <Typography variant="h5" component="h1" sx={{ fontWeight: 800, color: 'text.primary', mt: 2, mb: 0.5 }}>
            Willkommen zurück!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
            Bitte melde dich an, um fortzufahren.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="E-Mail oder Telefonnummer"
            name="contact"
            value={formData.contact}
            onChange={handleChange}
            margin="normal"
            placeholder="E-Mail oder Nummer eingeben"
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Passwort"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            margin="normal"
            placeholder="Dein Passwort"
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PasswordIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            startIcon={!loading ? <LoginIcon /> : null}
            sx={{ 
              mt: 3, 
              mb: 3, 
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 700
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Anmelden'}
          </Button>

          <Box textAlign="center">
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={onSwitchToReset}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                gap: 0.5,
                mb: 1,
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': { color: 'primary.main', textDecoration: 'none' }
              }}
            >
              <LockIcon fontSize="small" />
              Passwort vergessen?
            </Link>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="body2">
              Noch kein Konto?{' '}
              <Link
                component="button"
                type="button"
                onClick={onSwitchToRegister}
              >
                Jetzt registrieren
              </Link>
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default LoginForm;
